<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <!-- Loading State -->
        <v-card v-if="loading">
          <v-card-text class="text-center pa-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
            <p class="mt-4">Verifying invitation...</p>
          </v-card-text>
        </v-card>

        <!-- Invalid/Expired Invite -->
        <v-card v-else-if="!inviteValid || inviteError">
          <v-card-text class="text-center pa-8">
            <v-icon color="error" size="64">mdi-alert-circle</v-icon>
            <h2 class="mt-4">{{ inviteError || "Invalid or Expired Invitation" }}</h2>
            <p class="mt-2 text--secondary">
              This invitation link is no longer valid or has expired.
            </p>
            <v-btn color="primary" class="mt-4" @click="goToLogin">
              Go to Login
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- Valid Invite - Enrollment Form -->
        <v-card v-else>
          <v-card-title class="headline">
            Sales Representative Enrollment
          </v-card-title>
          <v-card-subtitle>
            You have been invited by <strong>{{ generalAgentName }}</strong>
          </v-card-subtitle>

          <v-card-text>
            <v-alert type="info" class="mb-4">
              Complete your enrollment to become a Sales Representative for {{ generalAgentName }}.
            </v-alert>

            <v-stepper v-model="step" vertical>
              <!-- Step 1: Personal Information -->
              <v-stepper-step :complete="step > 1" step="1">
                Personal Information
              </v-stepper-step>
              <v-stepper-content step="1">
                <v-form ref="form1" v-model="valid1">
                  <v-text-field
                    v-model="formData.fname"
                    label="First Name *"
                    outlined
                    dense
                    :rules="[v => !!v || 'First Name is required']"
                  ></v-text-field>

                  <v-text-field
                    v-model="formData.lname"
                    label="Last Name *"
                    outlined
                    dense
                    :rules="[v => !!v || 'Last Name is required']"
                  ></v-text-field>

                  <v-text-field
                    v-model="formData.taxId"
                    label="Federal Tax I.D. # or SSN (Optional)"
                    outlined
                    dense
                  ></v-text-field>

                  <v-btn color="primary" @click="validateStep(1)">
                    Continue
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <!-- Step 2: Address -->
              <v-stepper-step :complete="step > 2" step="2">
                Address Information
              </v-stepper-step>
              <v-stepper-content step="2">
                <v-form ref="form2" v-model="valid2">
                  <v-text-field
                    v-model="formData.address"
                    label="Address *"
                    outlined
                    dense
                    :rules="[v => !!v || 'Address is required']"
                  ></v-text-field>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="formData.city"
                        label="City *"
                        outlined
                        dense
                        :rules="[v => !!v || 'City is required']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="formData.state"
                        label="State *"
                        outlined
                        dense
                        value="FL"
                        :rules="[v => !!v || 'State is required']"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="formData.zipCode"
                        label="Zip Code *"
                        outlined
                        dense
                        :rules="[v => !!v || 'Zip Code is required']"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-text-field
                    v-model="formData.county"
                    label="County *"
                    outlined
                    dense
                    :rules="[v => !!v || 'County is required']"
                  ></v-text-field>

                  <v-btn text @click="step = 1">Back</v-btn>
                  <v-btn color="primary" @click="validateStep(2)">
                    Continue
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <!-- Step 3: Contact Information -->
              <v-stepper-step :complete="step > 3" step="3">
                Contact Information
              </v-stepper-step>
              <v-stepper-content step="3">
                <v-form ref="form3" v-model="valid3">
                  <v-text-field
                    v-model="formData.phoneNumber"
                    label="Phone *"
                    outlined
                    dense
                    :rules="[v => !!v || 'Phone is required']"
                    @input="formatPhone"
                  ></v-text-field>

                  <v-text-field
                    v-model="formData.fax"
                    label="Fax"
                    outlined
                    dense
                    @input="formatFax"
                  ></v-text-field>

                  <v-text-field
                    v-model="inviteInfo.email"
                    label="Email"
                    outlined
                    dense
                    disabled
                  ></v-text-field>

                  <v-btn text @click="step = 2">Back</v-btn>
                  <v-btn color="primary" @click="validateStep(3)">
                    Continue
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <!-- Step 4: Licensing -->
              <v-stepper-step :complete="step > 4" step="4">
                Licensing Information
              </v-stepper-step>
              <v-stepper-content step="4">
                <v-form ref="form4" v-model="valid4">
                  <v-switch
                    v-model="formData.isLicensed"
                    label="Is Sales Representative Licensed to sell Health Policies?"
                  ></v-switch>

                  <v-text-field
                    v-if="formData.isLicensed"
                    v-model="formData.licenseType"
                    label="License Type"
                    outlined
                    dense
                  ></v-text-field>

                  <v-file-input
                    v-if="formData.isLicensed"
                    v-model="licenseFile"
                    label="License Document"
                    accept=".pdf,.jpg,.jpeg,.png"
                    outlined
                    dense
                    @change="handleLicenseUpload"
                  ></v-file-input>

                  <v-btn text @click="step = 3">Back</v-btn>
                  <v-btn color="primary" @click="validateStep(4)">
                    Continue
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <!-- Step 5: Agreement -->
              <v-stepper-step :complete="step > 5" step="5">
                Agreement Information
              </v-stepper-step>
              <v-stepper-content step="5">
                <v-form ref="form5" v-model="valid5">
                  <v-menu
                    v-model="menuStartDate"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formData.agreementStartDate"
                        label="Agreement Start Date *"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                        :rules="[v => !!v || 'Agreement Start Date is required']"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formData.agreementStartDate"
                      @input="menuStartDate = false"
                    ></v-date-picker>
                  </v-menu>

                  <v-row>
                    <v-col cols="12">
                      <v-alert dense outlined type="error" v-model="signatureAlert">
                        Please sign your name below to continue
                      </v-alert>
                      <Signature ref="Signature" />
                    </v-col>
                  </v-row>

                  <v-btn text @click="step = 4">Back</v-btn>
                  <v-btn color="primary" @click="validateStep(5)">
                    Continue
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <!-- Step 6: User Account -->
              <v-stepper-step step="6">
                User Account
              </v-stepper-step>
              <v-stepper-content step="6">
                <v-form ref="form6" v-model="valid6">
                  <v-text-field
                    v-model="formData.password"
                    label="Password *"
                    type="password"
                    outlined
                    dense
                    :rules="[
                      v => !!v || 'Password is required',
                      v => (v && v.length >= 8) || 'Password must be at least 8 characters'
                    ]"
                  ></v-text-field>

                  <v-text-field
                    v-model="confirmPassword"
                    label="Confirm Password *"
                    type="password"
                    outlined
                    dense
                    :rules="[
                      v => !!v || 'Please confirm password',
                      v => v === formData.password || 'Passwords do not match'
                    ]"
                  ></v-text-field>

                  <v-btn text @click="step = 5">Back</v-btn>
                  <v-btn
                    color="success"
                    :loading="submitting"
                    :disabled="!valid6"
                    @click="submitEnrollment"
                  >
                    Submit Enrollment
                  </v-btn>
                </v-form>
              </v-stepper-content>
            </v-stepper>
          </v-card-text>
        </v-card>

        <!-- Success Dialog -->
        <v-dialog v-model="successDialog" max-width="500">
          <v-card>
            <v-card-title class="headline success--text">
              Enrollment Completed Successfully!
            </v-card-title>
            <v-card-text>
              <p>Your Sales Representative enrollment has been completed and is pending approval.</p>
              <p><strong>Agent ID:</strong> {{ submittedAgentID }}</p>
              <p>You are now linked to <strong>{{ generalAgentName }}</strong>.</p>
              <p>You will receive an email notification once your enrollment is approved.</p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="goToLogin">
                Go to Login
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Error Snackbar -->
        <v-snackbar v-model="errorSnackbar" color="error" :timeout="5000">
          {{ errorMessage }}
        </v-snackbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import Signature from "../components/new-enrollment-components/Signature.vue";

export default {
  name: "AgentEnrollmentInvite",
  components: {
    Signature,
  },
  data() {
    return {
      loading: true,
      inviteValid: false,
      inviteError: "",
      step: 1,
      valid1: false,
      valid2: false,
      valid3: false,
      valid4: false,
      valid5: false,
      valid6: false,
      submitting: false,
      signatureAlert: false,
      menuStartDate: false,
      confirmPassword: "",
      licenseFile: null,
      successDialog: false,
      submittedAgentID: "",
      errorSnackbar: false,
      errorMessage: "",
      inviteInfo: {
        email: "",
        name: "",
        phone: "",
      },
      generalAgentName: "",
      formData: {
        fname: "",
        lname: "",
        taxId: "",
        address: "",
        city: "",
        state: "FL",
        zipCode: "",
        county: "",
        phoneNumber: "",
        fax: "",
        isLicensed: false,
        licenseType: "",
        licenseDocument: "",
        agreementStartDate: new Date().toISOString().substr(0, 10),
        signature: "",
        password: "",
      },
    };
  },
  mounted() {
    this.verifyInvite();
  },
  methods: {
    async verifyInvite() {
      const token = this.$route.params.token;
      if (!token) {
        this.inviteValid = false;
        this.inviteError = "Invalid invitation link";
        this.loading = false;
        return;
      }

      try {
        const response = await axios.get(`/api/agent-enrollment/invite/${token}`);
        
        if (response.data.valid) {
          this.inviteValid = true;
          this.inviteInfo = response.data.invite;
          this.generalAgentName = response.data.generalAgent.name;
          
          // Pre-fill form with invite info
          if (this.inviteInfo.name) {
            const nameParts = this.inviteInfo.name.split(' ');
            this.formData.fname = nameParts[0] || '';
            this.formData.lname = nameParts.slice(1).join(' ') || '';
          }
          if (this.inviteInfo.phone) {
            this.formData.phoneNumber = this.inviteInfo.phone;
          }
        } else {
          this.inviteValid = false;
          this.inviteError = "Invalid or expired invitation";
        }
      } catch (error) {
        this.inviteValid = false;
        this.inviteError = error.response?.data?.message || "Error verifying invitation";
      } finally {
        this.loading = false;
      }
    },
    validateStep(stepNum) {
      const formRef = `form${stepNum}`;
      if (this.$refs[formRef].validate()) {
        if (stepNum === 5) {
          this.validateSignature();
        } else {
          this.step = stepNum + 1;
        }
      }
    },
    async validateSignature() {
      try {
        const signature = await this.$refs.Signature.submit();
        if (signature) {
          this.formData.signature = signature;
          this.signatureAlert = false;
          this.step = 6;
        } else {
          this.signatureAlert = true;
        }
      } catch (error) {
        this.signatureAlert = true;
      }
    },
    formatPhone() {
      let x = this.formData.phoneNumber
        .replace(/\D/g, "")
        .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      this.formData.phoneNumber = !x[2]
        ? x[1]
        : "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");
    },
    formatFax() {
      let x = this.formData.fax
        .replace(/\D/g, "")
        .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      this.formData.fax = !x[2]
        ? x[1]
        : "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");
    },
    handleLicenseUpload(file) {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.formData.licenseDocument = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    async submitEnrollment() {
      if (!this.$refs.form6.validate()) {
        return;
      }

      this.submitting = true;
      const token = this.$route.params.token;

      try {
        const response = await axios.post(
          `/api/agent-enrollment/sales-representative/enroll-via-invite/${token}`,
          this.formData
        );

        this.submittedAgentID = response.data.agentID;
        this.successDialog = true;
        this.submitting = false;
      } catch (error) {
        this.submitting = false;
        this.errorMessage =
          error.response?.data?.message ||
          "Error submitting enrollment. Please try again.";
        this.errorSnackbar = true;
      }
    },
    goToLogin() {
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
.v-stepper__header {
  background-color: #f5f5f5;
}
</style>


